import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Box, Container, Flex, Grid, Image, LinkButton } from '@tq/ui-components';
import { useIsMobile, useThemedValueSwitch } from '@tq/ui-components/dist/hooks';

import { AppBarMenu } from 'components';
import { isTQAdminState } from 'store';
import { useNavigate, useNavigation, useResetSearchState, useUpdateEffect } from 'hooks';
import { Breadcrumb, MobileMenu, TQAppBarMenu } from './components';
import logo from 'assets/logo.svg';
import tqLogo from 'assets/tq-logo.svg';
import { useLocation } from 'react-router-dom';
import { CUSTOM_TQ_COLOURS } from 'styles';

export type AppBarProps = {
  location: string;
  isAuthenticated: boolean;
};

export const AppBar: React.FC<AppBarProps> = ({ location, isAuthenticated }) => {
  const { t } = useTranslation('app_bar');
  const { isTQ, tvs } = useThemedValueSwitch();

  const { navigateToDashboard, navigateToHome, navigateToTrafficMonitor } = useNavigate();
  const isMobile = useIsMobile();
  const { isHomePage, isBreadcrumbPage } = useNavigation(location);
  const resetSearch = useResetSearchState();
  const isTQAdmin = useRecoilValue(isTQAdminState);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();

  const goToDashboard = () => {
    if (!isAuthenticated) {
      navigateToHome();
      return;
    }
    if (isTQAdmin) {
      navigateToTrafficMonitor();
    } else {
      resetSearch();
      navigateToDashboard();
    }
  };

  useUpdateEffect(() => {
    headerRef.current && headerRef.current.focus();
  }, [pathname]);

  return (
    <Grid
      position='fixed'
      top={0}
      zIndex={3}
      width='100%'
      tabIndex={-1}
      ref={headerRef}
      _focus={{ outline: 'none' }}
      data-testid='site-header'
    >
      <Box
        py={tvs({ ni: 3, tq: 4 })}
        maxH={tvs({ ni: 96, tq: 104 })}
        borderBottom={tvs({ ni: `1px solid ${COLOURS.blue[600]}` })}
        bg={tvs({ tq: CUSTOM_TQ_COLOURS.blue072C4D, ni: COLOURS.white })}
      >
        <Container px={{ base: 5, lg: 4, xl: 0 }}>
          <Flex justifyContent='space-between' alignItems='center'>
            {!isMobile || !isBreadcrumbPage ? (
              <LinkButton
                py='0'
                variant='secondary'
                onClick={goToDashboard}
                className='headerLogoLink'
                isDisabled={tvs({ tq: true })}
                sx={{
                  _disabled: {
                    pointerEvents: 'none',
                  },
                }}
              >
                <Image
                  width={{ base: 206, xl: 314 }}
                  height={{ base: 40, xl: 72 }}
                  src={tvs({ tq: tqLogo, ni: logo })}
                  alt={t('logo_alt', 'Consultee hub logo')}
                />
              </LinkButton>
            ) : (
              <Breadcrumb location={location} />
            )}
            {!isHomePage &&
              !isMobile &&
              isAuthenticated &&
              (isTQ ? (
                <TQAppBarMenu location={location} />
              ) : (
                <AppBarMenu location={location} />
              ))}
            {!isHomePage && isMobile && isAuthenticated && <MobileMenu />}
          </Flex>
        </Container>
      </Box>
      {isBreadcrumbPage && !isMobile && isAuthenticated && (
        <Box
          backgroundColor={COLOURS.white}
          borderBottom={{ base: 'none', lg: `1px solid ${COLOURS.gray[400]}` }}
        >
          <Container px={{ base: 5, lg: 4, xl: 0 }}>
            <Breadcrumb location={location} />
          </Container>
        </Box>
      )}
    </Grid>
  );
};
